import React, { useState } from "react";
import { Grid } from "@mui/material";
import KpiCard from "components/KpiCard";
import MDBox from "components/MDBox";
import IconButton from "@mui/material/IconButton";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { useDrop } from "react-dnd";
import AddIcon from "@mui/icons-material/Add";
import AddKpiDialog from "components/AddKpiDialog"; // Import the dialog
import { useMaterialUIController } from "context";
import { useAppTheme } from "context/themeContext";

const ItemTypes = { KPI: "kpi" };

const PerspectiveContainer = ({
  title,
  perspectiveId,
  kpis,
  moveKpi,
  addKpi,
  removeKpi,
  allKpis,
  deleteMode,
  toDeleteKpis,
  handleSelectKpi,
  canEdit,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState(null); // Track the selected KPI
  const { primaryColor } = useAppTheme();
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;

  const [, drop] = useDrop(() => ({
    accept: ItemTypes.KPI,
    drop: (item) => moveKpi(item.id, perspectiveId),
  }));

  // Open dialog for adding a new KPI
  const handleAddKpi = () => {
    setSelectedKpi(null); // Reset selected KPI for new entry
    setIsDialogOpen(true);
  };

  // Open dialog for editing a KPI
  const handleEditKpi = (kpi) => {
    if (!canEdit) return;
    setSelectedKpi(kpi); // Set selected KPI
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedKpi(null); // Reset after closing
  };

  return (
    <MDBox
      item
      xs={12}
      md={3}
      ref={drop}
      sx={{
        p: 2,
        m: 1,
        flexGrow: 1,
        border: "2px solid " + primaryColor,
        borderRadius: 2,
        minHeight: "300px",
        maxHeight: "300px",
        background: darkMode ? "#333" : "#fafafa",
        boxShadow: darkMode
          ? "0px 4px 6px rgba(255, 255, 255, 0.1)"
          : "0px 4px 6px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header with Title and Add Button */}
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          flexWrap: "wrap", // Allows title to wrap without pushing content out
        }}
      >
        <MDTypography
          variant="h6"
          sx={{
            flexGrow: 1, // Allows it to expand inside the available space
            wordBreak: "break-word", // Ensures long words break properly
            minWidth: 0, // Prevents it from expanding uncontrollably
          }}
        >
          {title}
        </MDTypography>
        <IconButton
          onClick={handleAddKpi}
          disabled={!canEdit}
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            color: primaryColor,
            ":hover": {
              color: "#fff",
              backgroundColor: primaryColor,
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <AddIcon />
        </IconButton>
      </MDBox>

      {/* KPI Box */}
      <MDBox
        ref={drop}
        sx={{
          overflowY: "auto",
          flexGrow: 1, // Ensures the KPI list adapts dynamically
          maxHeight: "220px",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          paddingRight: "8px",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f0f0f0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cfcfcf",
            borderRadius: "10px",
            transition: "background 0.3s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#b0b0b0",
          },
        }}
      >
        {kpis
          .slice()
          .reverse()
          .map((kpi) => (
            <KpiCard
              key={kpi.kpiNameId}
              kpi={kpi}
              moveKpi={moveKpi}
              onEdit={() => handleEditKpi(kpi)}
              deleteMode={deleteMode}
              toDeleteKpis={toDeleteKpis}
              handleSelectKpi={handleSelectKpi}
              canEdit={canEdit}
            />
          ))}
      </MDBox>

      {/* Add/Edit KPI Dialog */}
      <AddKpiDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        perspectiveId={perspectiveId}
        addKpi={addKpi}
        kpiToEdit={selectedKpi}
        removeKpi={removeKpi}
        allKpis={allKpis}
      />
    </MDBox>
  );
};

// Add PropTypes validation
PerspectiveContainer.propTypes = {
  title: PropTypes.string.isRequired,
  perspectiveId: PropTypes.number.isRequired,
  kpis: PropTypes.arrayOf(
    PropTypes.shape({
      kpiNameId: PropTypes.number.isRequired,
      kpiName: PropTypes.string.isRequired,
      perspectiveId: PropTypes.number.isRequired,
    })
  ).isRequired,
  moveKpi: PropTypes.func.isRequired,
  addKpi: PropTypes.func.isRequired,
  removeKpi: PropTypes.func.isRequired,
  allKpis: PropTypes.array.isRequired,
  deleteMode: PropTypes.bool.isRequired,
  toDeleteKpis: PropTypes.array.isRequired,
  handleSelectKpi: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default PerspectiveContainer;
